// Unicode subsets and special characters for the Symbol dialog

export const SYMBOL_SUBSETS = [
  { id: 'basic-latin', name: 'Basic Latin', from: 0x0020, to: 0x007e },
  { id: 'latin-1', name: 'Latin-1 Supplement', from: 0x00a0, to: 0x00ff },
  { id: 'latin-ext-a', name: 'Latin Extended-A', from: 0x0100, to: 0x017f },
  { id: 'latin-ext-b', name: 'Latin Extended-B', from: 0x0180, to: 0x024f },
  { id: 'greek', name: 'Greek and Coptic', from: 0x0391, to: 0x03c9 },
  { id: 'cyrillic', name: 'Cyrillic', from: 0x0410, to: 0x044f },
  { id: 'hebrew', name: 'Hebrew', from: 0x05d0, to: 0x05ea },
  { id: 'punctuation', name: 'General Punctuation', from: 0x2010, to: 0x205e },
  { id: 'currency', name: 'Currency Symbols', from: 0x20a0, to: 0x20bf },
  { id: 'letterlike', name: 'Letterlike Symbols', from: 0x2100, to: 0x214f },
  { id: 'number-forms', name: 'Number Forms', from: 0x2150, to: 0x218b },
  { id: 'arrows', name: 'Arrows', from: 0x2190, to: 0x21ff },
  { id: 'math', name: 'Mathematical Operators', from: 0x2200, to: 0x22ff },
  { id: 'misc-technical', name: 'Miscellaneous Technical', from: 0x2300, to: 0x23ff },
  { id: 'box-drawing', name: 'Box Drawing', from: 0x2500, to: 0x257f },
  { id: 'block', name: 'Block Elements', from: 0x2580, to: 0x259f },
  { id: 'geometric', name: 'Geometric Shapes', from: 0x25a0, to: 0x25ff },
  { id: 'misc-symbols', name: 'Miscellaneous Symbols', from: 0x2600, to: 0x26ff },
  { id: 'dingbats', name: 'Dingbats', from: 0x2700, to: 0x27bf },
  { id: 'misc-symbols-arrows', name: 'Miscellaneous Symbols and Arrows', from: 0x2b00, to: 0x2b59 },
  { id: 'emoticons', name: 'Emoticons', from: 0x1f600, to: 0x1f64f },
  { id: 'transport', name: 'Transport and Map Symbols', from: 0x1f680, to: 0x1f6c5 },
];

export const charsForSubset = (subset) => {
  const out = [];
  for (let cp = subset.from; cp <= subset.to; cp++) {
    // skip control / unassigned obvious ranges
    if (cp >= 0x2028 && cp <= 0x202f) continue;
    if (cp >= 0x2060 && cp <= 0x206f) continue;
    out.push(String.fromCodePoint(cp));
  }
  return out;
};

export const codePointHex = (ch) => (ch.codePointAt(0) || 0).toString(16).toUpperCase().padStart(4, '0');

export const SPECIAL_CHARACTERS = [
  { char: '\u2014', name: 'Em Dash', shortcut: 'Alt+Ctrl+Num -' },
  { char: '\u2013', name: 'En Dash', shortcut: 'Ctrl+Num -' },
  { char: '\u2011', name: 'Nonbreaking Hyphen', shortcut: 'Ctrl+Shift+_' },
  { char: '\u00ad', name: 'Optional Hyphen', shortcut: 'Ctrl+-' },
  { char: '\u2003', name: 'Em Space', shortcut: '' },
  { char: '\u2002', name: 'En Space', shortcut: '' },
  { char: '\u2009', name: '1/4 Em Space', shortcut: '' },
  { char: '\u00a0', name: 'Nonbreaking Space', shortcut: 'Ctrl+Shift+Space' },
  { char: '\u00a9', name: 'Copyright', shortcut: 'Alt+Ctrl+C' },
  { char: '\u00ae', name: 'Registered', shortcut: 'Alt+Ctrl+R' },
  { char: '\u2122', name: 'Trademark', shortcut: 'Alt+Ctrl+T' },
  { char: '\u00a7', name: 'Section', shortcut: '' },
  { char: '\u00b6', name: 'Paragraph', shortcut: '' },
  { char: '\u2026', name: 'Ellipsis', shortcut: 'Alt+Ctrl+.' },
  { char: '\u2018', name: 'Single Opening Quote', shortcut: 'Ctrl+`,`' },
  { char: '\u2019', name: 'Single Closing Quote', shortcut: "Ctrl+',' " },
  { char: '\u201c', name: 'Double Opening Quote', shortcut: 'Ctrl+`,"' },
  { char: '\u201d', name: 'Double Closing Quote', shortcut: "Ctrl+',\" " },
  { char: '\u2022', name: 'Bullet', shortcut: '' },
  { char: '\u00b7', name: 'Middle Dot', shortcut: '' },
  { char: '\u00b0', name: 'Degree Sign', shortcut: '' },
  { char: '\u00bd', name: 'One Half', shortcut: '' },
  { char: '\u00bc', name: 'One Quarter', shortcut: '' },
  { char: '\u00be', name: 'Three Quarters', shortcut: '' },
  { char: '\u2020', name: 'Dagger', shortcut: '' },
  { char: '\u2021', name: 'Double Dagger', shortcut: '' },
  { char: '\u2713', name: 'Check Mark', shortcut: '' },
  { char: '\u271d', name: 'Latin Cross', shortcut: '' },
  { char: '\u2720', name: 'Maltese Cross', shortcut: '' },
  { char: '\u00d7', name: 'Multiplication Sign', shortcut: '' },
  { char: '\u00f7', name: 'Division Sign', shortcut: '' },
  { char: '\u20ac', name: 'Euro Sign', shortcut: 'Alt+Ctrl+E' },
  { char: '\u00a3', name: 'Pound Sign', shortcut: '' },
  { char: '\u00a5', name: 'Yen Sign', shortcut: '' },
];

export const UNICODE_NAMES = Object.fromEntries(SPECIAL_CHARACTERS.map((s) => [s.char, s.name]));

export const RECENT_SYMBOLS_KEY = 'wp.recentSymbols';
export const getRecentSymbols = () => {
  try {
    const list = JSON.parse(localStorage.getItem(RECENT_SYMBOLS_KEY) || 'null');
    return Array.isArray(list) && list.length ? list : ['\u00bd', '\u00be', '\u00b0', '\u2022', '\u2153', '\u221a', '\u00b7', '\u222b', '\u014d', '\u00e9', '\u215d', '\u00f1', '\u00e0', '\u00ed', '\u00ea', '\u2014'];
  } catch {
    return [];
  }
};
export const pushRecentSymbol = (ch) => {
  const list = [ch, ...getRecentSymbols().filter((c) => c !== ch)].slice(0, 16);
  localStorage.setItem(RECENT_SYMBOLS_KEY, JSON.stringify(list));
  return list;
};
